import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { useTranslation } from '@gem/modules/i18n';
import { IChipsItem } from '@gem/ui-kit';
import { debounce, CancelPromiseHandler, makeCancellablePromise } from '@gem/utils';

import Tasks from 'APP/Tasks';
import { IChipsWithAvatarItem } from 'APP/components/ChipsWithAvatar/ChipsWithAvatar';
import { MIN_SEARCH_LENGTH } from 'APP/constants/search';
import { GroupType } from 'APP/model/group/groupModel.types';
import Entities from 'STORE';
import { User } from 'STORE/Users/User/User';

export interface ISelectWorkspaceMembersPresenter {
  members: User[];
  isLoading: boolean;
  cursor?: string;
  searchText: string;
  cancelLoadMore: CancelPromiseHandler | null;
  search: () => void;
  canSelectMore: boolean;
  selectedMembersIds: string[];
  hasMore: boolean;
  chips: IChipsWithAvatarItem[];
  isChannel: boolean;
  isSearch: boolean;
  init(): void;
  loadMore(isInit?: boolean): Promise<void>;
  onReloadMembersList(): void;
  getIsSelected(candidate: User): boolean;
  onSearch(searchText: string): void;
  removeChips(item: IChipsItem): void;
  toggleContact(candidate: User): void;
  resetSearch(): void;
}

interface ISelectWorkspaceMembersPresenterProps {
  selectedMembersIds: string[];
  targetGroupType: GroupType;
  groupId: string;
  setSelectedIds(ids: string[]): void;
}

const SEARCH_DELAY = 200;
const CHIPS_LIMIT = 50;

export function useSelectWorkspaceMembersPresenter(
  props: ISelectWorkspaceMembersPresenterProps
): ISelectWorkspaceMembersPresenter {
  const { t } = useTranslation();

  const presenter = useLocalObservable<ISelectWorkspaceMembersPresenter>(() => ({
    cursor: undefined,
    isLoading: false,
    members: [],
    cancelLoadMore: null,
    searchText: '',
    selectedMembersIds: [],
    isChannel: [GroupType.ChannelOpen, GroupType.ChannelClosed].includes(props.targetGroupType),

    get chips(): IChipsWithAvatarItem[] {
      return presenter.selectedMembersIds.map((id) => {
        const contact = Entities.usersStore.getUserById(id);

        return {
          id: contact?.id || '',
          data: contact,
          avatarTitle: contact?.displayName || '',
          avatarUrl: contact?.avatarUrl || '',
          title: contact?.avatarTitle || '',
          avatarColorIndex: contact?.avatarColorIndex || '1',
        };
      });
    },

    get isSearch(): boolean {
      return presenter.searchText.length >= MIN_SEARCH_LENGTH;
    },

    resetSearch(): void {
      presenter.members = [];
      presenter.cursor = undefined;
      presenter.isLoading = false;
    },

    get canSelectMore(): boolean {
      return presenter.selectedMembersIds.length < CHIPS_LIMIT;
    },

    toggleContact(candidate: User): void {
      if (
        (!presenter.isChannel && !candidate.canAddToGroupMe) ||
        (presenter.isChannel && !candidate.canAddToPublicMe)
      ) {
        Tasks.app.showAlert(t('privacy_settings_add_contact_alert'));
        return;
      }

      const isExist = presenter.selectedMembersIds.includes(candidate.id);

      if (isExist) {
        props.setSelectedIds(presenter.selectedMembersIds.filter((id) => id !== candidate.id));
      } else {
        if (!presenter.canSelectMore) {
          Tasks.app.showToast(t('common_group_alert_user_many_add'));
          return;
        }

        props.setSelectedIds([...presenter.selectedMembersIds, candidate.id]);
      }
    },

    removeChips(item: IChipsItem): void {
      props.setSelectedIds(presenter.selectedMembersIds.filter((id) => id !== item.id));
    },

    getIsSelected(candidate: User): boolean {
      return presenter.selectedMembersIds.includes(candidate.id);
    },

    onSearch(searchText: string): void {
      presenter.searchText = searchText;
      if (!presenter.isSearch) {
        return;
      }

      presenter.resetSearch();
      presenter.search();
    },

    async init(): Promise<void> {
      presenter.resetSearch();
      await presenter.loadMore();
    },

    search: debounce(async () => {
      await presenter.loadMore();
    }, SEARCH_DELAY),

    async loadMore(): Promise<void> {
      if (presenter.cancelLoadMore) {
        presenter.cancelLoadMore();
      }

      presenter.isLoading = true;

      const { promise: getWorkspaceMembers, cancel } = makeCancellablePromise(
        this.isSearch
          ? Tasks.search.searchAddUsersToGroup({
              query: presenter.searchText,
              cursor: presenter.cursor,
              groupId: props.groupId,
            })
          : Tasks.group.getAddUsersToGroup({
              cursor: presenter.cursor,
              groupId: props.groupId,
            })
      );

      presenter.cancelLoadMore = cancel;

      const workspaceMembersMembersData = await getWorkspaceMembers;

      if (workspaceMembersMembersData) {
        const { users, cursor } = workspaceMembersMembersData;

        presenter.members.push(...users);
        presenter.cursor = cursor;
      }

      presenter.cancelLoadMore = null;
      presenter.isLoading = false;
    },

    get hasMore(): boolean {
      return !!presenter.cursor;
    },

    onMemberRemove(userId: string): void {
      presenter.members = presenter.members.filter((member) => member.id !== userId);
    },

    onReloadMembersList(): void {
      presenter.init();
    },
  }));

  useEffect(() => {
    if (!presenter.isSearch) {
      presenter.init();
    }
  }, [presenter.isSearch]);

  useEffect(() => {
    presenter.selectedMembersIds = props.selectedMembersIds;
  }, [props.selectedMembersIds]);

  return presenter;
}
